/**
 * Session Activity Service
 * 
 * Tracks user activity (mouse, keyboard, scroll, touch) to determine if user is active.
 * Uses debouncing to avoid excessive updates and localStorage for cross-tab synchronization.
 */

export class SessionActivityService {
  constructor() {
    this.lastActivityTime = Date.now()
    this.debounceTimer = null
    this.DEBOUNCE_DELAY = 10000 // 10 seconds - don't update more frequently
    this.STORAGE_KEY = 'session_last_activity_time'
    this.listeners = []
    this.initialized = false
    this.trackingPaused = false
    this.pauseTimeout = null
  }

  /**
   * Initialize activity tracking
   * Sets up event listeners for user interactions
   */
  initialize() {
    if (this.initialized) {
      console.log('⚠️ SessionActivityService already initialized')
      return
    }

    // Load last activity from storage (for page refresh or cross-tab)
    const stored = localStorage.getItem(this.STORAGE_KEY)
    if (stored) {
      this.lastActivityTime = parseInt(stored, 10)
      console.log('📍 Restored last activity time from storage:', new Date(this.lastActivityTime))
    }

    // Register event listeners for user activity
    // Use passive: true for scroll/touch events to improve performance
    const events = [
      { name: 'mousemove', options: { passive: true } },
      { name: 'mousedown', options: { passive: true } },
      { name: 'keydown', options: { passive: true } },
      { name: 'scroll', options: { passive: true } },
      { name: 'touchstart', options: { passive: true } },
      { name: 'click', options: { passive: true } }
    ]

    events.forEach(({ name, options }) => {
      window.addEventListener(name, this.handleActivity.bind(this), options)
    })

    // Listen for activity updates from other tabs via localStorage
    window.addEventListener('storage', this.handleStorageChange.bind(this))

    this.initialized = true
    console.log('✅ SessionActivityService initialized')
  }

  /**
   * Pause activity tracking for a specified duration (for testing)
   * @param {number} durationMs - Duration to pause tracking in milliseconds
   */
  pauseTrackingFor(durationMs) {
    this.trackingPaused = true
    console.log(`📍 Activity tracking paused for ${durationMs}ms`)
    
    if (this.pauseTimeout) {
      clearTimeout(this.pauseTimeout)
    }
    
    this.pauseTimeout = setTimeout(() => {
      this.trackingPaused = false
      console.log('📍 Activity tracking resumed')
    }, durationMs)
  }

  /**
   * Handle user activity event (debounced)
   */
  handleActivity() {
    // Skip recording if tracking is paused
    if (this.trackingPaused) return

    // If debounce timer is active, ignore this event
    if (this.debounceTimer) return

    // Set debounce timer to prevent too frequent updates
    this.debounceTimer = setTimeout(() => {
      this.recordActivity()
      this.debounceTimer = null
    }, this.DEBOUNCE_DELAY)
  }

  /**
   * Record activity timestamp
   * Updates memory, localStorage, and notifies listeners
   */
  recordActivity() {
    // Skip recording if tracking is paused
    if (this.trackingPaused) {
      console.log('📍 Activity recording skipped - tracking paused for testing')
      return
    }
    
    this.lastActivityTime = Date.now()
    
    // Persist to localStorage for cross-tab sync and page refresh
    localStorage.setItem(this.STORAGE_KEY, this.lastActivityTime.toString())
    
    // Notify all subscribers
    this.notifyListeners()
    
    // console.log('📍 Activity recorded:', new Date(this.lastActivityTime))
  }

  /**
   * Force record activity without debouncing
   * Useful for explicit user actions like clicking "I'm here" button
   */
  forceRecordActivity() {
    // Clear any pending debounce timer
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer)
      this.debounceTimer = null
    }
    
    this.recordActivity()
  }

  /**
   * Get timestamp of last activity
   * @returns {number} Timestamp in milliseconds
   */
  getLastActivityTime() {
    return this.lastActivityTime
  }

  /**
   * Get time elapsed since last activity
   * @returns {number} Milliseconds since last activity
   */
  getTimeSinceLastActivity() {
    return Date.now() - this.lastActivityTime
  }

  /**
   * Check if user was active within threshold
   * @param {number} thresholdMs - Threshold in milliseconds
   * @returns {boolean} True if active within threshold
   */
  isActiveWithinThreshold(thresholdMs) {
    return this.getTimeSinceLastActivity() < thresholdMs
  }

  /**
   * Subscribe to activity updates
   * @param {Function} callback - Called when activity is recorded
   * @returns {Function} Unsubscribe function
   */
  subscribe(callback) {
    this.listeners.push(callback)
    
    // Return unsubscribe function
    return () => {
      this.listeners = this.listeners.filter(cb => cb !== callback)
    }
  }

  /**
   * Notify all subscribers of activity
   */
  notifyListeners() {
    this.listeners.forEach(callback => {
      try {
        callback(this.lastActivityTime)
      } catch (error) {
        console.error('❌ Error in activity listener:', error)
      }
    })
  }

  /**
   * Handle localStorage change from other tabs
   * Updates local state when another tab records activity
   */
  handleStorageChange(event) {
    if (event.key === this.STORAGE_KEY && event.newValue) {
      const newActivityTime = parseInt(event.newValue, 10)
      
      // Only update if it's newer than our current time
      if (newActivityTime > this.lastActivityTime) {
        this.lastActivityTime = newActivityTime
        this.notifyListeners()
        console.log('📍 Activity synced from another tab:', new Date(this.lastActivityTime))
      }
    }
  }

  /**
   * Get formatted time since last activity
   * @returns {string} Human-readable time (e.g., "5 minutes ago")
   */
  getFormattedTimeSinceActivity() {
    const ms = this.getTimeSinceLastActivity()
    const seconds = Math.floor(ms / 1000)
    const minutes = Math.floor(seconds / 60)
    const hours = Math.floor(minutes / 60)

    if (hours > 0) {
      return `${hours} hour${hours !== 1 ? 's' : ''} ago`
    } else if (minutes > 0) {
      return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`
    } else {
      return `${seconds} second${seconds !== 1 ? 's' : ''} ago`
    }
  }

  /**
   * Cleanup - remove event listeners
   * Call this when shutting down or unmounting
   */
  destroy() {
    const events = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'click']
    events.forEach(event => {
      window.removeEventListener(event, this.handleActivity)
    })
    
    window.removeEventListener('storage', this.handleStorageChange)
    
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer)
      this.debounceTimer = null
    }
    
    if (this.pauseTimeout) {
      clearTimeout(this.pauseTimeout)
      this.pauseTimeout = null
    }
    
    this.initialized = false
    console.log('✅ SessionActivityService destroyed')
  }
}

// Export singleton with lazy getter pattern
let sessionActivityServiceInstance = null

export function getSessionActivityService() {
    if (!sessionActivityServiceInstance) {
        sessionActivityServiceInstance = new SessionActivityService()
    }
    return sessionActivityServiceInstance
}

// Lazy getter for backward compatibility with method binding
export const sessionActivityService = new Proxy({}, {
    get(target, prop) {
        const instance = getSessionActivityService()
        const value = instance[prop]
        // Bind methods to preserve 'this' context when called through Proxy
        if (typeof value === 'function') {
            return value.bind(instance)
        }
        return value
    },
    set(target, prop, value) {
        const instance = getSessionActivityService()
        instance[prop] = value
        return true
    }
})
